import moment from 'moment';

import { convertNumToCurrencyStr, formatDate, unCamelCase } from '../../utils/globalHelpers';
import {
  CLAIMS_DEPT_AUTHOR,
  CLAIM_DETAILS_PAGE_HEADER,
  NEW_CLAIM_INBOX_SUBJECT_LINE,
  claimDetailsStepDictionary,
  defaultContactInfo,
} from './constants';

/**
 * Given a claim object, returns an array of its supplemental disbursements
 * in ascending order of their disbursement numbers.
 * If given undefined or null, returns empty array.
 * @param {object} selectedClaim claim object or undefined
 */
export const getSupplementalDisbursements = (selectedClaim) => {
  if (!selectedClaim) return [];
  const { completeDisbursements, otherDisbursements } = selectedClaim;
  const allDisbursements = [...completeDisbursements, ...otherDisbursements];
  return allDisbursements.filter(
    ({ disbursementType }) => disbursementType === 'Supplemental',
  ).sort(({ disbursementNumber: a }, { disbursementNumber: b }) => a - b);
};

/**
 * Given a claim object, returns the disbursement number of the claim's first
 * supplemental disbursement. If given undefined, or if a claim has no supplemental disbursements,
 * then returns null
 * @param {object} claim
 */
export const getDefaultSelectedDisbursementOption = (claim) => {
  const supplementalDisbursements = getSupplementalDisbursements(claim);
  const { disbursementNumber = null } = supplementalDisbursements[0] || {};
  return disbursementNumber;
};

export function generateDashboardNextStep({
  disbursementNumber,
  disbursementStatus,
  disbursementType,
  isSmallClaim,
}) {
  const claimSizeStatus = isSmallClaim ? 'IsSmallClaim' : 'IsNotSmallClaim';
  const claimDetailsStep = claimDetailsStepDictionary[claimSizeStatus][disbursementType];
  const isActive = !(disbursementStatus === 'Complete' || disbursementType === 'Exception');
  let message = '';
  if (disbursementStatus === 'AuthorizationRejected') {
    message = 'Please call your dedicated representative as soon as possible to resolve the issue.';
  }
  if (disbursementType === 'Exception') {
    message = 'Please call your dedicated representative as soon as possible.';
  }
  return {
    claimDetailsStep,
    disbursementNumber,
    disbursementStep: 1,
    isActive,
    message,
  };
}

/**
 * Given an array of unformatted claim objects, returns an array of formatted and
 * sorted claim objects (to be stored in redux).
 * @param {array} claims
 * @param {string} lastLoginDate
 * @param {array || null} readClaimNumbers
 * @returns {array}
 * [
*   {
*     activeDisbursements: [],
*     claimNumber: 'string',
*     claimStatus: ('Open' | 'Close' | 'Renew')
*     claimTitle: 'string',
*     claimType: 'string',
*     completedDisbursements: [],
*     documents: [] | null,
*     lossDate: 'string',
*     pointOfContact: {},
*     reimbursedAmount: 'string',
*     reimbursedTotal: 'string',
*   },
*  ]
 */
export const formatClaims = (claims, lastLoginDate, readClaimNumbers) => {
  const formattedClaims = claims.map((claim) => {
    const {
      claimNumber,
      claimStatus,
      completeDisbursements,
      documents,
      // TODO: uncomment when inspections are added to the claims inbox/detail pages
      // inspections,
      lossAmount,
      lossDate,
      lossTypes,
      otherDisbursements,
      pointOfContact,
      totalReimbursed,
    } = claim;
    const defaultSupplementalDisbursementNumber = getDefaultSelectedDisbursementOption(claim);
    const isSmallClaim = false;
    const activeDisbursements = otherDisbursements.map(({
      checkNumber,
      disbursementAmount,
      disbursementAuthorizationRemarks,
      disbursementDate,
      disbursementNumber,
      disbursementStatus,
      disbursementType,
    }) => {
      const actionRequired = disbursementStatus === 'AuthorizationRejected';
      const amount = convertNumToCurrencyStr(disbursementAmount, 2);
      const checkDetail = checkNumber ? {} : !!checkNumber;
      const date = disbursementDate;
      const isUnreadClaimEvent =
        checkIsUnreadClaim(date, lastLoginDate, claimNumber, readClaimNumbers);
      const nextStepDisbursementNumber = disbursementType === 'Supplemental' ?
        disbursementNumber :
        defaultSupplementalDisbursementNumber;
      const nextStep = generateDashboardNextStep({
        disbursementNumber: nextStepDisbursementNumber,
        disbursementStatus,
        disbursementType,
        isSmallClaim,
      });
      const subtitle = actionRequired ? disbursementAuthorizationRemarks : 'Process initiated';
      const title = `${disbursementType} Disbursement`;
      return {
        actionRequired,
        amount,
        checkDetail,
        date,
        formattedDate: formatDate(date, null, 'LL'),
        isUnreadClaimEvent,
        nextStep,
        number: disbursementNumber,
        subtitle,
        title,
      };
    });
    let claimTitle;
    let claimType;
    if (lossTypes) {
      claimTitle = lossTypes.join(', ');
      claimType = lossTypes.length === 1 ? lossTypes[0] : 'Other';
    } else {
      claimTitle = 'Other';
      claimType = 'Other';
    }
    const completedDisbursements = completeDisbursements.map(({
      disbursementAmount,
      disbursementDate,
      disbursementNumber,
      disbursementType,
    }) => {
      const date = disbursementDate;
      const isUnreadClaimEvent =
        checkIsUnreadClaim(date, lastLoginDate, claimNumber, readClaimNumbers);
      const amount = convertNumToCurrencyStr(disbursementAmount, 2);
      const title = `${disbursementType} Disbursement`;
      return {
        amount,
        date,
        formattedDate: formatDate(date, null, 'LL'),
        isUnreadClaimEvent,
        number: disbursementNumber,
        title,
      };
    });
    const documentEvents = documents ? documents.items.map(({
      documentNumber,
      documentReceivedDate,
      documentStatus,
      documentType,
    }) => {
      const date = formatDate(documentReceivedDate, null, 'LL');
      return {
        date,
        number: documentNumber,
        status: documentStatus,
        title: documentType,
      };
    }) : [];
    const reimbursedAmount = convertNumToCurrencyStr(lossAmount, 2);
    const reimbursedTotal = convertNumToCurrencyStr(totalReimbursed, 2);

    return {
      activeDisbursements,
      claimNumber,
      claimStatus,
      claimTitle,
      claimType,
      completedDisbursements,
      documentEvents,
      // TODO: uncomment when inspections are added to the claims inbox/detail pages
      // inspections,
      lossDate,
      pointOfContact,
      reimbursedAmount,
      reimbursedTotal,
    };
  });
  const formattedAndSortedClaims =
    formattedClaims.sort((a, b) => moment(b.lossDate).valueOf() - moment(a.lossDate).valueOf());
  return formattedAndSortedClaims;
};

const checkIsUnreadClaim = (date, lastLoginDate, claimNumber, readClaimNumbers) => {
  if (!readClaimNumbers || !readClaimNumbers.includes(claimNumber)) {
    return moment(date).isAfter(lastLoginDate);
  }
  return false;
};

/**
 * @description Iterates through the active and completed disbursements, documents, and
 * inspections of a claim and returns an array of objects with the date and title
 * of each disbursement event. If given undefined or null, returns empty array.
 *
 * @param {array} activeDisbursements
 * @param {array} completedDisbursements
 * @param {array} documents
 * @param {array} inspections
 * @returns {array} claimEvents
 */
export const getClaimEvents = ({
  activeDisbursements,
  completedDisbursements,
  lastLoginDate,
  lossDate,
  // TODO: uncomment when inspections are added to the claims inbox/detail pages
  // inspections,
}) => {
  const claimEvents = [];
  if (lossDate) {
    claimEvents.push({
      date: lossDate,
      isUnreadClaimEvent: moment(lossDate).isAfter(lastLoginDate),
      title: NEW_CLAIM_INBOX_SUBJECT_LINE,
    });
  }
  activeDisbursements.forEach(({ date, isUnreadClaimEvent, title }) => {
    claimEvents.push({ date, isUnreadClaimEvent, title });
  });
  completedDisbursements.forEach(({ date, isUnreadClaimEvent, title }) => {
    claimEvents.push({ date, isUnreadClaimEvent, title });
  });
  return claimEvents;
};

export const formatClaimDetails = ({
  activeDisbursements,
  claimNumber,
  claimTitle,
  completedDisbursements,
  documentEvents,
  // TODO: uncomment when inspections are added to the claims inbox/detail pages
  // inspections,
  pointOfContact = {},
  reimbursedAmount,
  reimbursedTotal,
}) => {
  const { contactName, phoneExtension } = pointOfContact;
  const {
    extension: fallbackExt,
    heading: contactCardHeading,
    hours,
    phone: phoneNumber,
  } = defaultContactInfo;
  const active = activeDisbursements.map(({
    actionRequired,
    date,
    formattedDate,
    nextStep: { message },
    number,
    subtitle,
    title,
  }) => ({
    date,
    formattedDate,
    number,
    text: `${actionRequired ? message : subtitle}`,
    title,
  }));
  const completed = completedDisbursements.map(({
    amount,
    date,
    formattedDate,
    number,
    title,
  }) => ({
    date,
    formattedDate,
    number,
    text: `Payment Amount: ${amount}`,
    title,
  }));
  const disbursements =
    [...active, ...completed].sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());

  return {
    claimNumber,
    claimType: claimTitle,
    disbursed: `${reimbursedTotal} of ${reimbursedAmount}`,
    disbursements,
    documents: documentEvents ? documentEvents.map(({
      date,
      number,
      status,
      title,
    }) => ({
      date,
      number,
      status,
      type: `${unCamelCase(title)}`,
    })) : [],
    header: CLAIM_DETAILS_PAGE_HEADER,
    pointOfContact: {
      contactCardHeading,
      contactName: `${contactName || CLAIMS_DEPT_AUTHOR}`,
      ext: `Ext.${phoneExtension}` || '',
      fallbackExt,
      hours,
      phoneNumber,
    },
  };
};
